import { Percent, RefreshCw, Save } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { CargoCommissionCompanyRow, CargoCommissionSettings } from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { isCeo } from '../../utils/roles';

function bpsToPercent(bps: number): string {
  return (bps / 100).toFixed(2);
}

function percentToBps(percent: string): number | null {
  const n = Number.parseFloat(percent.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || n > 25) return null;
  return Math.round(n * 100);
}

export function CargoCommissionSection() {
  const { role } = useAuth();
  const ceo = isCeo(role);

  const [data, setData] = useState<CargoCommissionSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'default' | string>('default');
  const [defaultPercent, setDefaultPercent] = useState('1.20');
  const [companyPercent, setCompanyPercent] = useState('');
  const [useDefault, setUseDefault] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await api.getCargoCommissionSettings();
      setData(settings);
      setDefaultPercent(bpsToPercent(settings.default_commission_bps));
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (ceo) void load();
  }, [ceo, load]);

  const selectedCompany: CargoCommissionCompanyRow | null =
    tab === 'default' || !data
      ? null
      : data.companies.find((c) => c.id === tab) ?? null;

  useEffect(() => {
    if (!selectedCompany) return;
    if (selectedCompany.commission_bps == null) {
      setUseDefault(true);
      setCompanyPercent('');
    } else {
      setUseDefault(false);
      setCompanyPercent(bpsToPercent(selectedCompany.commission_bps));
    }
  }, [selectedCompany]);

  if (!ceo) {
    return (
      <div className="card commission-panel">
        <p className="commission-empty">Commission settings are available to the CEO only.</p>
      </div>
    );
  }

  async function saveDefault() {
    const bps = percentToBps(defaultPercent);
    if (bps == null) {
      setMessage('Enter a valid percent between 0% and 25%.');
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await api.patchCargoDefaultCommission(bps);
      setMessage('Platform default saved.');
      await load();
    } catch (e) {
      setMessage(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveCompany() {
    if (!selectedCompany) return;
    setSaving(true);
    setMessage(null);
    try {
      let bps: number | null = null;
      if (!useDefault) {
        const parsed = percentToBps(companyPercent);
        if (parsed == null) {
          setMessage('Enter a valid company rate between 0% and 25%.');
          setSaving(false);
          return;
        }
        bps = parsed;
      }
      await api.patchCargoCompanyCommission(selectedCompany.id, bps);
      setMessage(`Rate saved for ${selectedCompany.name}.`);
      await load();
    } catch (e) {
      setMessage(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="section-header cargo-section-header">
        <div>
          <h1 className="console-title">Only Crypto Commissions</h1>
          <p className="console-sub">
            Configure platform and partner rates. The fee is deducted from the merchant; the buyer
            pays the order total (e.g. 100 USDT order → 98.8 to merchant, 1.2 to Only Crypto at
            1.2%).
          </p>
        </div>
        <div className="cargo-header-actions">
          <button
            type="button"
            className="btn-outline btn-inline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'cargo-spin' : undefined} aria-hidden />
            Refresh
          </button>
        </div>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}
      {message ? (
        <p className={`section-error ${message.includes('saved') ? 'form-success' : 'form-error'}`}>
          {message}
        </p>
      ) : null}

      <div className="commission-tabs" role="tablist" aria-label="Commission scope">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'default'}
          className={`commission-tab${tab === 'default' ? ' active' : ''}`}
          onClick={() => setTab('default')}
        >
          Platform default
        </button>
        {(data?.companies ?? []).map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={tab === c.id}
            className={`commission-tab${tab === c.id ? ' active' : ''}`}
            onClick={() => setTab(c.id)}
            title={c.company_code}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <p className="commission-empty">Loading commission settings…</p>
      ) : tab === 'default' ? (
        <div className="card cargo-panel commission-panel">
          <div className="cargo-panel-head">
            <div className="cargo-panel-title-wrap">
              <Percent size={16} color="var(--onyx-gold)" aria-hidden />
              <h2 className="cargo-panel-title">Platform default rate</h2>
            </div>
          </div>
          <div className="commission-panel-body">
            <p className="commission-panel-lead">
              Applied to all Only Crypto partners without a company-specific override. Server env
              fallback: CARGO_COMMISSION_BPS.
            </p>
            <div className="commission-form-grid">
              <label className="field-label" htmlFor="default-pct">
                <span>Commission rate (%)</span>
                <input
                  id="default-pct"
                  className="commission-input"
                  value={defaultPercent}
                  onChange={(e) => setDefaultPercent(e.target.value)}
                  inputMode="decimal"
                  autoComplete="off"
                />
                <span className="commission-bps-hint">
                  {percentToBps(defaultPercent) ?? '—'} basis points
                </span>
              </label>
              <div className="commission-actions">
                <button
                  type="button"
                  className="btn-primary btn-inline"
                  disabled={saving}
                  onClick={() => void saveDefault()}
                >
                  <Save size={16} aria-hidden />
                  Save platform default
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : selectedCompany ? (
        <div className="card cargo-panel commission-panel">
          <div className="cargo-panel-head">
            <div className="cargo-panel-title-wrap">
              <Percent size={16} color="var(--onyx-gold)" aria-hidden />
              <h2 className="cargo-panel-title">{selectedCompany.name}</h2>
            </div>
          </div>
          <div className="commission-panel-body">
            <p className="commission-panel-meta">
              Code {selectedCompany.company_code} · {selectedCompany.status} · effective{' '}
              {bpsToPercent(selectedCompany.effective_commission_bps)}%
            </p>
            <label className="field-check">
              <input
                type="checkbox"
                checked={useDefault}
                onChange={(e) => setUseDefault(e.target.checked)}
              />
              <span>
                Use platform default (
                {data ? bpsToPercent(data.default_commission_bps) : '—'}%)
              </span>
            </label>
            {!useDefault ? (
              <div className="commission-form-grid">
                <label className="field-label" htmlFor="company-pct">
                  <span>Partner commission rate (%)</span>
                  <input
                    id="company-pct"
                    className="commission-input"
                    value={companyPercent}
                    onChange={(e) => setCompanyPercent(e.target.value)}
                    inputMode="decimal"
                    autoComplete="off"
                  />
                  <span className="commission-bps-hint">
                    {percentToBps(companyPercent) ?? '—'} basis points
                  </span>
                </label>
              </div>
            ) : null}
            <div className="commission-actions">
              <button
                type="button"
                className="btn-primary btn-inline"
                disabled={saving}
                onClick={() => void saveCompany()}
              >
                <Save size={16} aria-hidden />
                Save partner rate
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
